import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { forum } from "@/lib/api";
import { useAuth } from "@/context/AuthProvider";

function fmt(iso) {
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function CommunityThread() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { user, hasPerm } = useAuth();
  const [thread, setThread] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editingThread, setEditingThread] = useState(false);
  const [threadDraft, setThreadDraft] = useState({ title: "", body: "" });
  const [editingPostId, setEditingPostId] = useState(null);
  const [postDraft, setPostDraft] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await forum.thread(threadId);
      setThread(res.thread);
      setPosts(res.posts);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [threadId]);

  const canModerate = hasPerm("user.manage");

  const startEditThread = () => {
    setThreadDraft({ title: thread.title, body: thread.body });
    setEditingThread(true);
  };
  const saveThread = async () => {
    setBusy(true);
    setError("");
    try {
      const updated = await forum.updateThread(threadId, threadDraft);
      setThread({ ...thread, ...updated });
      setEditingThread(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save changes");
    } finally {
      setBusy(false);
    }
  };
  const deleteThread = async () => {
    if (!window.confirm("Delete this thread and all its replies? This can't be undone.")) return;
    setBusy(true);
    try {
      await forum.deleteThread(threadId);
      navigate("/resources/community");
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not delete thread");
      setBusy(false);
    }
  };

  const startEditPost = (p) => { setEditingPostId(p.id); setPostDraft(p.body); };
  const savePost = async (postId) => {
    setBusy(true);
    setError("");
    try {
      const updated = await forum.updatePost(postId, postDraft);
      setPosts(posts.map((p) => (p.id === postId ? { ...p, ...updated } : p)));
      setEditingPostId(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save changes");
    } finally {
      setBusy(false);
    }
  };
  const deletePost = async (postId) => {
    if (!window.confirm("Delete this reply?")) return;
    setBusy(true);
    try {
      await forum.deletePost(postId);
      setPosts(posts.filter((p) => p.id !== postId));
      setThread({ ...thread, reply_count: Math.max(0, thread.reply_count - 1) });
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not delete reply");
    } finally {
      setBusy(false);
    }
  };

  const submitReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    setError("");
    try {
      const post = await forum.reply(threadId, reply.trim());
      setPosts([...posts, post]);
      setThread({ ...thread, reply_count: thread.reply_count + 1 });
      setReply("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not post reply");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="page"><div className="center-load"><span className="spinner" /> Loading…</div></div>;
  }
  if (notFound || !thread) {
    return (
      <div className="page" data-testid="page-community-thread-notfound">
        <div className="page-subtitle">Thread not found. <Link to="/resources/community">Back to Community Forum</Link></div>
      </div>
    );
  }

  const isThreadAuthor = thread.author_id === user?.id;

  return (
    <div className="page" data-testid="page-community-thread">
      <div className="page-header">
        <div>
          <Link to="/resources/community" className="forum-back-link">← Community Forum</Link>
          <div className="page-title" style={{ marginTop: 4 }}>{thread.title}</div>
          <div className="page-subtitle">{thread.category} · {thread.reply_count} {thread.reply_count === 1 ? "reply" : "replies"}</div>
        </div>
      </div>

      <div className="forum-op" data-testid="forum-op">
        <div className="forum-post-meta">
          <span><strong>{thread.author_name}</strong> · {fmt(thread.created_at)}{thread.edited_at && " · edited"}</span>
          {(isThreadAuthor || canModerate) && !editingThread && (
            <span className="flex gap-8">
              {isThreadAuthor && <button className="btn btn-ghost btn-sm" onClick={startEditThread} data-testid="edit-thread-btn">Edit</button>}
              <button className="btn btn-ghost btn-sm" style={{ color: "var(--danger)" }} onClick={deleteThread} disabled={busy} data-testid="delete-thread-btn">Delete</button>
            </span>
          )}
        </div>
        {editingThread ? (
          <div className="forum-composer">
            <input value={threadDraft.title} onChange={(e) => setThreadDraft({ ...threadDraft, title: e.target.value })} data-testid="edit-thread-title" />
            <textarea rows={4} value={threadDraft.body} onChange={(e) => setThreadDraft({ ...threadDraft, body: e.target.value })} data-testid="edit-thread-body" />
            {error && <div style={{ color: "var(--danger)", fontSize: 12 }}>{error}</div>}
            <div className="flex gap-8">
              <button className="btn btn-primary btn-sm" onClick={saveThread} disabled={busy} data-testid="save-thread-btn">Save</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditingThread(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="forum-post-body">{thread.body}</div>
        )}
      </div>

      <div className="forum-post-list">
        {posts.map((p) => {
          const isAuthor = p.author_id === user?.id;
          return (
            <div className="forum-post" key={p.id} data-testid={`forum-post-${p.id}`}>
              <div className="forum-post-meta">
                <span><strong>{p.author_name}</strong> · {fmt(p.created_at)}{p.edited_at && " · edited"}</span>
                {(isAuthor || canModerate) && editingPostId !== p.id && (
                  <span className="flex gap-8">
                    {isAuthor && <button className="btn btn-ghost btn-sm" onClick={() => startEditPost(p)}>Edit</button>}
                    <button className="btn btn-ghost btn-sm" style={{ color: "var(--danger)" }} onClick={() => deletePost(p.id)} disabled={busy}>Delete</button>
                  </span>
                )}
              </div>
              {editingPostId === p.id ? (
                <div className="forum-composer">
                  <textarea rows={3} value={postDraft} onChange={(e) => setPostDraft(e.target.value)} data-testid={`edit-post-body-${p.id}`} />
                  {error && <div style={{ color: "var(--danger)", fontSize: 12 }}>{error}</div>}
                  <div className="flex gap-8">
                    <button className="btn btn-primary btn-sm" onClick={() => savePost(p.id)} disabled={busy}>Save</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditingPostId(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="forum-post-body">{p.body}</div>
              )}
            </div>
          );
        })}
        {posts.length === 0 && (
          <div style={{ padding: "16px 0", color: "var(--text-secondary)", fontSize: 13 }}>No replies yet — be the first.</div>
        )}
      </div>

      <div className="forum-composer" data-testid="reply-composer">
        <textarea
          rows={3}
          placeholder="Write a reply…"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          data-testid="reply-input"
        />
        {error && !editingThread && !editingPostId && <div style={{ color: "var(--danger)", fontSize: 12 }}>{error}</div>}
        <div className="flex gap-8">
          <button className="btn btn-primary btn-sm" onClick={submitReply} disabled={busy || !reply.trim()} data-testid="reply-submit-btn">
            {busy ? <><span className="spinner white" /> Posting…</> : "Post reply"}
          </button>
        </div>
      </div>
    </div>
  );
}
